import React, { useState, useEffect, useCallback } from 'react';
import './MigrationTable.css';
import { MigrationTableProps, MigrationData, MigrationHistoryResponse } from '../types';
import { migrationApi } from '../services/migrationApi';
import RestartMigrationModal from './RestartMigrationModal';
import SuccessModal from './SuccessModal';

const formatDisplayValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '--';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? '--' : trimmed;
  }

  return String(value);
};

const formatDateValue = (value: string | null | undefined): string => {
  if (!value) {
    return '--';
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? '--' : parsedDate.toLocaleString();
};

const hasStringValue = (value: string | null | undefined): boolean => {
  return typeof value === 'string' && value.trim() !== '';
};

const MigrationTable: React.FC<MigrationTableProps> = ({ onMigrationClick, searchTerm, refreshTrigger }) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [migrationData, setMigrationData] = useState<MigrationData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [environmentFilter, setEnvironmentFilter] = useState<string | null>(null);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState<boolean>(false);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);
  const [isRestartModalOpen, setIsRestartModalOpen] = useState<boolean>(false);
  const [selectedMigrationForRestart, setSelectedMigrationForRestart] = useState<MigrationData | null>(null);
  const [restartingMigrationId, setRestartingMigrationId] = useState<number | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Fetch migration history data
  const fetchMigrationHistory = useCallback(async (page: number, limit: number, fetchAll: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // When filtering, fetch all data to allow proper filtering across all pages
      const fetchLimit = fetchAll ? 100 : limit;
      const fetchPage = fetchAll ? 1 : page;
      
      const data: MigrationHistoryResponse = searchTerm && searchTerm.trim().length > 0
        ? await migrationApi.searchMigrationHistory(searchTerm.trim(), fetchPage, fetchLimit)
        : await migrationApi.getMigrationHistory(fetchPage, fetchLimit);
      setMigrationData(data.data);
      setTotalItems(data.totalItems);
      setTotalPages(data.totalPages);
    } catch (err: any) {
      console.error('Error fetching migration history:', err);
      
      // Handle specific error cases - show API error messages
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.detail?.error) {
        setError(err.response.data.detail.error);
      } else if (err.response?.status === 403) {
        setError('Access forbidden. You do not have permission to view migration history.');
      } else {
        setError('Failed to fetch migration history. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Fetch data when component mounts or when page/itemsPerPage/searchTerm changes
  useEffect(() => {
    // When filter is active, fetch all data; otherwise use pagination
    const shouldFetchAll = environmentFilter !== null;
    fetchMigrationHistory(currentPage, itemsPerPage, shouldFetchAll);
  }, [currentPage, itemsPerPage, searchTerm, environmentFilter, refreshTrigger, fetchMigrationHistory]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [environmentFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.environment-filter-container')) {
        setFilterDropdownOpen(false);
      }
    };

    if (filterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [filterDropdownOpen]);

  const handleSort = (field: string): void => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort the data based on current sort field and direction
  const getSortedData = (data: MigrationData[]): MigrationData[] => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'application':
          aValue = (a.app || '').toLowerCase();
          bValue = (b.app || '').toLowerCase();
          break;
        case 'date':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Filter data by environment
  const getFilteredData = (data: MigrationData[]): MigrationData[] => {
    if (!environmentFilter) return data;
    return data.filter(item => item.env === environmentFilter);
  };
  
  // Use the data directly from API response (already paginated) and apply filtering and sorting
  const filteredData = getFilteredData(migrationData);
  const currentPageData = getSortedData(filteredData);

  // Calculate pagination info based on filtered data
  const filteredTotalItems = environmentFilter ? filteredData.length : totalItems;
  const filteredTotalPages = environmentFilter ? Math.ceil(filteredData.length / itemsPerPage) : totalPages;
  const startItem = filteredTotalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = filteredTotalItems === 0 ? 0 : Math.min(currentPage * itemsPerPage, filteredTotalItems);
  
  // For filtered data, we need to paginate the filtered results
  const paginatedFilteredData = environmentFilter 
    ? currentPageData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : currentPageData;

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number): void => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const copyToClipboard = (url: string): void => {
    if (hasStringValue(url)) {
      navigator.clipboard.writeText(url);
    }
  };

  const handleDownloadReport = async (reportId: string | undefined, migrationId: number): Promise<void> => {
    if (!reportId) {
      console.error('No report_id available for this migration');
      alert('No report available for this migration.');
      return;
    }

    try {
      setDownloadingReportId(reportId);
      const { blob, contentType } = await migrationApi.generateReport(reportId);
      
      // Determine file extension from content type
      let fileExtension = 'pdf'; // default
      if (contentType.includes('pdf')) {
        fileExtension = 'pdf';
      } else if (contentType.includes('excel') || contentType.includes('spreadsheet')) {
        fileExtension = 'xlsx';
      } else if (contentType.includes('csv')) {
        fileExtension = 'csv';
      } else if (contentType.includes('json')) {
        fileExtension = 'json';
      }
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${migrationId}_${reportId}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading report:', err);
      // Optionally show an error message to the user
      if (err.response?.data?.message) {
        alert(`Failed to download report: ${err.response.data.message}`);
      } else {
        alert('Failed to download report. Please try again.');
      }
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleRestartMigration = async (migration: MigrationData): Promise<void> => {
    try {
      setRestartingMigrationId(migration.id);
      
      // Use migration._id as migration_id parameter
      const response = await migrationApi.retryMigration(migration._id);
      
      if (response.status_code === 200 && response.message) {
        // Success - open the restart modal
        setSelectedMigrationForRestart(migration);
        setIsRestartModalOpen(true);
      }
    } catch (err: any) {
      console.error('Error restarting migration:', err);
      // Extract error message from API response
      let errorMsg = 'Failed to restart migration. Please try again.';
      if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setErrorMessage(errorMsg);
      setIsErrorModalOpen(true);
    } finally {
      setRestartingMigrationId(null);
    }
  };

  const handleRestartModalClose = (wasCompleted?: boolean): void => {
    setIsRestartModalOpen(false);
    setSelectedMigrationForRestart(null);
    
    // Refresh migration list if migration was completed
    if (wasCompleted) {
      const shouldFetchAll = environmentFilter !== null;
      fetchMigrationHistory(currentPage, itemsPerPage, shouldFetchAll);
    }
  };

  const getStatusIcon = (status?: string): React.ReactNode => {
    const normalizedStatus = status?.toLowerCase();
    if (normalizedStatus === 'success') {
      return <span className="status-dot success"></span>;
    }
    if (normalizedStatus === 'failed') {
      return <span className="status-dot failed"></span>;
    }
    return null;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="migration-table-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading migration history...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="migration-table-container">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button 
            className="retry-button"
            onClick={() => {
              const shouldFetchAll = environmentFilter !== null;
              fetchMigrationHistory(currentPage, itemsPerPage, shouldFetchAll);
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state when no records are returned (works for both search, history, and filtered results)
  const hasData = environmentFilter 
    ? (filteredData && filteredData.length > 0) 
    : migrationData.length > 0;
  if (!loading && !error && !hasData) {
    return (
      <div className="migration-table-container">
        <div className="table-wrapper">
          <div className="empty-state">
            <div className="empty-icon" aria-hidden="true">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2"/>
                <path d="M8 10.5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="empty-text">
              <div className="empty-title">No records found</div>
              <div className="empty-subtitle">Try adjusting your search or filters.</div>
            </div>
          </div>
        </div>
        <div className="table-footer">
          <div className="pagination-info">0 - 0 of 0 items</div>
          <div className="pagination-controls">
            <select className="items-per-page-select" value={itemsPerPage} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleItemsPerPageChange(Number(e.target.value))}>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <div className="page-navigation">
              <button className="nav-button" disabled>&lt;</button>
              <button className="nav-button" disabled>&gt;</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="migration-table-container">
      <div className="table-wrapper">
        <table className="migration-table">
          <thead>
            <tr className="table-header">
              <th className={`table-header-cell sortable ${sortField === 'id' ? 'active' : ''}`} onClick={() => handleSort('id')}>
                ID
                <span className="sort-icon">
                  {sortField === 'id' ? (
                    sortDirection === 'asc' ? '↑' : '↓'
                  ) : (
                    '↕'
                  )}
                </span>
              </th>
              <th className={`table-header-cell sortable ${sortField === 'application' ? 'active' : ''}`} onClick={() => handleSort('application')}>
                Application
                <span className="sort-icon">
                  {sortField === 'application' ? (
                    sortDirection === 'asc' ? '↑' : '↓'
                  ) : (
                    '↕'
                  )}
                </span>
              </th>
              <th className="table-header-cell environment-filter-header">
                <div className="environment-filter-container">
                  Environment
                  <button 
                    className={`filter-icon-button ${environmentFilter ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilterDropdownOpen(!filterDropdownOpen);
                    }}
                    title="Filter by environment"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 7h18M7 12h10M10 17h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  {filterDropdownOpen && (
                    <div className="environment-filter-dropdown">
                      <div 
                        className={`filter-option ${environmentFilter === null ? 'selected' : ''}`}
                        onClick={() => {
                          setEnvironmentFilter(null);
                          setFilterDropdownOpen(false);
                        }}
                      >
                        All
                      </div>
                      <div 
                        className={`filter-option ${environmentFilter === 'TEST' ? 'selected' : ''}`}
                        onClick={() => {
                          setEnvironmentFilter('TEST');
                          setFilterDropdownOpen(false);
                        }}
                      >
                        TEST
                      </div>
                      <div 
                        className={`filter-option ${environmentFilter === 'APPROVED' ? 'selected' : ''}`}
                        onClick={() => {
                          setEnvironmentFilter('APPROVED');
                          setFilterDropdownOpen(false);
                        }}
                      >
                        APPROVED
                      </div>
                      <div 
                        className={`filter-option ${environmentFilter === 'DEV' ? 'selected' : ''}`}
                        onClick={() => {
                          setEnvironmentFilter('DEV');
                          setFilterDropdownOpen(false);
                        }}
                      >
                        DEV
                      </div>
                      <div 
                        className={`filter-option ${environmentFilter === 'PROD' ? 'selected' : ''}`}
                        onClick={() => {
                          setEnvironmentFilter('PROD');
                          setFilterDropdownOpen(false);
                        }}
                      >
                        PROD
                      </div>
                      <div 
                        className={`filter-option ${environmentFilter === 'QA' ? 'selected' : ''}`}
                        onClick={() => {
                          setEnvironmentFilter('QA');
                          setFilterDropdownOpen(false);
                        }}
                      >
                        QA
                      </div>
                      <div 
                        className={`filter-option ${environmentFilter === 'STG' ? 'selected' : ''}`}
                        onClick={() => {
                          setEnvironmentFilter('STG');
                          setFilterDropdownOpen(false);
                        }}
                      >
                        STG
                      </div>
                    </div>
                  )}
                </div>
              </th>
              <th className="table-header-cell">Code Base URL</th>
              <th className={`table-header-cell sortable ${sortField === 'date' ? 'active' : ''}`} onClick={() => handleSort('date')}>
                Date
                <span className="sort-icon">
                  {sortField === 'date' ? (
                    sortDirection === 'asc' ? '↑' : '↓'
                  ) : (
                    '↕'
                  )}
                </span>
              </th>
              <th className={`table-header-cell sortable ${sortField === 'status' ? 'active' : ''}`} onClick={() => handleSort('status')}>
                Status
                <span className="sort-icon">
                  {sortField === 'status' ? (
                    sortDirection === 'asc' ? '↑' : '↓'
                  ) : (
                    '↕'
                  )}
                </span>
              </th>
              <th className="table-header-cell">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedFilteredData.map((row, index) => {
              const isSuccess = row.status?.toLowerCase() === 'success';
              return (
              <tr
                key={row._id}
                className={`table-row ${!isSuccess ? 'table-row-unclickable' : ''}`}
                onClick={() => isSuccess && onMigrationClick && onMigrationClick(row)}
              >
                <td className="table-cell id-cell">{formatDisplayValue(row.id)}</td>
                <td className="table-cell application-cell">{formatDisplayValue(row.app)}</td>
                <td className="table-cell environment-cell">{formatDisplayValue(row.env)}</td>
                <td className="table-cell url-cell">
                  {hasStringValue(row.url) ? (
                    <div className="url-container">
                      <a 
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="url-text"
                        title={row.url}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                          // Prevent the row click handler from firing when the URL is clicked
                          e.stopPropagation();
                        }}
                      >
                        {row.url}
                      </a>
                      <button 
                        className="copy-button"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          copyToClipboard(row.url);
                        }}
                        title="Copy URL"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  ) : (
                    '--'
                  )}
                </td>
                <td className="table-cell date-cell">{formatDateValue(row.created_at)}</td>
                <td className="table-cell status-cell">
                  {hasStringValue(row.status) ? (
                    <span className={`status-text ${row?.status?.toLowerCase()}`}>
                      {getStatusIcon(row.status)}
                      {row?.status?.charAt(0).toUpperCase() + row?.status?.slice(1).toLowerCase()}
                    </span>
                  ) : (
                    '--'
                  )}
                </td>
                <td className="table-cell actions-cell">
                  {row.status?.toLowerCase() === 'failed' && (
                    <button 
                      className="action-button restart-button" 
                      title="Restart Migration"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleRestartMigration(row);
                      }}
                      disabled={restartingMigrationId === row.id}
                    >
                      {restartingMigrationId === row.id ? (
                        <div className="loading-spinner-small"></div>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
                        </svg>
                      )}
                    </button>
                  )}
                  <button 
                    className="action-button download-button-small" 
                    title={isSuccess ? 'Download' : 'Report available only for successful migrations'}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      handleDownloadReport(row.report_id, row.id);
                    }}
                    disabled={!isSuccess || !row.report_id || downloadingReportId === row.report_id}
                  >
                    {downloadingReportId === row.report_id ? (
                      <div className="loading-spinner-small"></div>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                      </svg>
                    )}
                  </button>
                   {/* More options button code commented out for now */}
                  {/* <button 
                    className="action-button more-button" 
                    title="More options"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/>
                    </svg>
                  </button> */}
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
      
      <div className="table-footer">
        <div className="pagination-info">
          {startItem} - {endItem} of {filteredTotalItems} items
        </div>
        <div className="pagination-controls">
          <select 
            className="items-per-page-select"
            value={itemsPerPage}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleItemsPerPageChange(Number(e.target.value))}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
          <div className="page-navigation">
            <button 
              className="nav-button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              &lt;
            </button>
            <button 
              className="nav-button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === filteredTotalPages || filteredTotalPages === 0}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      {/* Restart Migration Modal */}
      {selectedMigrationForRestart && (
        <RestartMigrationModal
          isOpen={isRestartModalOpen}
          onClose={handleRestartModalClose}
          migrationData={selectedMigrationForRestart}
        />
      )}

      {/* Error Modal */}
      <SuccessModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="Error"
        message={errorMessage}
        variant="error"
        confirmText="OK"
      />
    </div>
  );
};

export default MigrationTable;
